import { useLayoutEffect, useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertTriangle,
  Box,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Equal,
  Info,
  Layers,
  Lock,
  MapPin,
  Minus,
  Pencil,
  Plus,
  RotateCcw,
  ScanBarcode,
  Smartphone,
  Undo2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ReceivingAdjustmentReasonEnum,
  ReceivingItemTypeEnum,
} from '../types/receiving.types';
import type {
  EditReceivedQuantityRequest,
  PutAwayReversalDto,
  ReceivingDocumentItemDto,
  SerialEditDto,
} from '../types/receiving.types';

/** Semantic status tone used across the reconciliation readout, rows and change list. */
type Tone = 'success' | 'warning' | 'destructive' | 'info';

const REASON_OPTIONS: { value: ReceivingAdjustmentReasonEnum; label: string }[] = [
  { value: ReceivingAdjustmentReasonEnum.CountError, label: 'Miscount at receiving' },
  { value: ReceivingAdjustmentReasonEnum.WrongBin, label: 'Wrong bin selected' },
  { value: ReceivingAdjustmentReasonEnum.DamageFound, label: 'Damaged / rejected units' },
  { value: ReceivingAdjustmentReasonEnum.WrongSerial, label: 'Wrong serial scanned' },
  { value: ReceivingAdjustmentReasonEnum.DuplicateEntry, label: 'Duplicate / double entry' },
  { value: ReceivingAdjustmentReasonEnum.Other, label: 'Other (add a note)' },
];

const TYPE_META: Record<ReceivingItemTypeEnum, { label: string; Icon: typeof Box }> = {
  [ReceivingItemTypeEnum.InventoryItem]: { label: 'Bin-only', Icon: Box },
  [ReceivingItemTypeEnum.LotNumbered]: { label: 'Lot-numbered', Icon: Layers },
  [ReceivingItemTypeEnum.Serialized]: { label: 'Serialized', Icon: ScanBarcode },
};

interface DraftPlacement {
  id: number;
  bin: string;
  lot: string;
  /** Currently-placed qty (reducible). */
  qty: number;
  /** Effective qty at sheet open — the base a reduction/reversal gives back. */
  originalQty: number;
  reversed: boolean;
  /** Already reversed when the sheet opened (a prior correction) — shown as an audit row. */
  committed: boolean;
}

interface DraftSerial {
  id: number;
  sn: string;
  originalSn: string;
  bin: string;
  removed: boolean;
  committed: boolean;
}

interface Draft {
  received: number;
  placements: DraftPlacement[];
  serials: DraftSerial[];
  reason: ReceivingAdjustmentReasonEnum | null;
  note: string;
}

interface ChangeLine {
  icon: typeof Pencil;
  tone: Tone;
  text: string;
}

// Static tone→class maps (Tailwind JIT only sees complete literal class strings — never build them
// dynamically like `text-${tone}`, which would be purged from the bundle).
const TONE_TEXT: Record<Tone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-info',
};
const TONE_BG_SOFT: Record<Tone, string> = {
  success: 'bg-success/15',
  warning: 'bg-warning/15',
  destructive: 'bg-destructive/15',
  info: 'bg-info/15',
};
const READOUT_BG: Record<Tone, string> = {
  success: 'bg-success/5',
  warning: 'bg-warning/5',
  destructive: 'bg-destructive/5',
  info: 'bg-info/5',
};

function cloneFrom(item: ReceivingDocumentItemDto): Draft {
  const isSerial = item.itemType === ReceivingItemTypeEnum.Serialized;
  const placements: DraftPlacement[] = [];
  const serials: DraftSerial[] = [];

  for (const h of item.putAwayHistories ?? []) {
    const remaining = Math.max(0, h.quantity - (h.quantityReversed ?? 0));
    const reversedAtLoad = remaining <= 0;
    if (isSerial) {
      serials.push({
        id: h.id,
        sn: h.serialNumber ?? '',
        originalSn: h.serialNumber ?? '',
        bin: h.binCode,
        removed: reversedAtLoad,
        committed: reversedAtLoad,
      });
    } else {
      placements.push({
        id: h.id,
        bin: h.binCode,
        lot: h.lotNumber ?? '',
        qty: reversedAtLoad ? 0 : remaining,
        originalQty: reversedAtLoad ? h.quantity : remaining,
        reversed: reversedAtLoad,
        committed: reversedAtLoad,
      });
    }
  }

  return { received: item.receivedQuantity, placements, serials, reason: null, note: '' };
}

interface ReceivingCorrectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ReceivingDocumentItemDto | null;
  /** Approximate PO ceiling for received qty (exact limit re-checked server-side). */
  poCap: number;
  readOnly: boolean;
  submitting: boolean;
  onSubmit: (itemId: number, request: EditReceivedQuantityRequest) => Promise<void>;
}

export function ReceivingCorrectionSheet({
  open,
  onOpenChange,
  item,
  poCap,
  readOnly,
  submitting,
  onSubmit,
}: ReceivingCorrectionSheetProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useLayoutEffect(() => {
    // Clone into the local draft when the sheet opens (or its target item changes). useLayoutEffect
    // runs before paint, so the first frame already shows the item's data — no empty flash. The draft
    // is intentionally NOT cleared on close, so the slide-out animation keeps its content.
    if (open && item) {
      setDraft(cloneFrom(item));
      setConfirmOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  if (!item || !draft) {
    // Before the first open (or after the target is cleared) the sheet is closed — mount an empty
    // controlled shell so Radix can still run the open/close transition.
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-[680px]" />
      </Sheet>
    );
  }

  const isService = item.isServiceItem;
  const isSerial = item.itemType === ReceivingItemTypeEnum.Serialized && !isService;
  const isBinLot = !isSerial && !isService;
  const unit = item.unitOfMeasure || 'pcs';
  const type = TYPE_META[item.itemType] ?? TYPE_META[ReceivingItemTypeEnum.InventoryItem];

  const patch = (fn: (d: Draft) => Draft) => setDraft((d) => (d ? fn(d) : d));

  // ── Derived (read `draft` directly so the compiler tracks them) ──────────────
  const placed = isService
    ? draft.received
    : isSerial
      ? draft.serials.filter((s) => !s.removed).length
      : draft.placements.reduce((a, p) => a + (p.reversed ? 0 : p.qty), 0);
  const still = draft.received - placed;
  const over = placed > draft.received;
  const overCap = draft.received > poCap;

  // Post-edit duplicate serials (trim + case-insensitive) — flags every colliding row.
  const dupIds = new Set<number>();
  {
    const seen = new Map<string, number>();
    for (const s of draft.serials) {
      if (s.removed) continue;
      const k = s.sn.trim().toUpperCase();
      if (!k) continue;
      const prev = seen.get(k);
      if (prev !== undefined) {
        dupIds.add(s.id);
        dupIds.add(prev);
      } else {
        seen.set(k, s.id);
      }
    }
  }

  const changes: ChangeLine[] = [];
  if (draft.received !== item.receivedQuantity) {
    changes.push({
      icon: Pencil,
      tone: 'info',
      text: `Received ${item.receivedQuantity} → ${draft.received} ${unit}${draft.received < item.receivedQuantity ? ' (miscount corrected)' : ''}`,
    });
  }
  for (const p of draft.placements) {
    if (p.reversed && !p.committed) {
      changes.push({
        icon: Undo2,
        tone: 'destructive',
        text: `Reversed ${p.originalQty} ${unit} from ${p.bin}${p.lot ? ` · Lot ${p.lot}` : ''}`,
      });
    } else if (!p.reversed && p.qty < p.originalQty) {
      changes.push({
        icon: Minus,
        tone: 'warning',
        text: `Reduced ${p.bin}: ${p.originalQty} → ${p.qty} ${unit}`,
      });
    }
  }
  for (const s of draft.serials) {
    if (s.removed && !s.committed) {
      changes.push({ icon: X, tone: 'destructive', text: `Removed serial ${s.originalSn} from ${s.bin}` });
    } else if (!s.removed && s.sn.trim() !== s.originalSn) {
      changes.push({ icon: Pencil, tone: 'info', text: `Serial ${s.originalSn} → ${s.sn.trim()}` });
    }
  }

  const hasEmptySerial = isSerial && draft.serials.some((s) => !s.removed && !s.sn.trim());
  const canSave =
    !readOnly &&
    !submitting &&
    !over &&
    draft.received <= poCap &&
    draft.received >= 0 &&
    !(isSerial && (dupIds.size > 0 || hasEmptySerial)) &&
    changes.length > 0 &&
    draft.reason !== null &&
    !(draft.reason === ReceivingAdjustmentReasonEnum.Other && !draft.note.trim());

  const readTone: Tone = over ? 'destructive' : still === 0 ? 'success' : 'warning';
  const activeBins = draft.placements.filter((p) => !p.reversed).length;
  const fullyReversed = isBinLot && draft.placements.length > 0 && activeBins === 0;

  let receivedMsg = '';
  let receivedMsgTone: Tone = 'info';
  if (overCap) {
    receivedMsg = `Exceeds PO allowance — max ${poCap} ${unit}.`;
    receivedMsgTone = 'destructive';
  } else if (over) {
    receivedMsg = `Lower than what is placed. Reverse or reduce ${placed - draft.received} ${unit} below to save.`;
    receivedMsgTone = 'destructive';
  } else if (draft.received < item.receivedQuantity) {
    receivedMsg = `Lowered from ${item.receivedQuantity}. Make sure placement matches.`;
    receivedMsgTone = 'info';
  }

  let saveTip = 'Review the changes before saving';
  if (over) saveTip = 'Placed exceeds received — reduce placement first';
  else if (overCap) saveTip = 'Received exceeds PO allowance';
  else if (isSerial && dupIds.size > 0) saveTip = 'Fix the duplicate serial first';
  else if (isSerial && hasEmptySerial) saveTip = 'Serial numbers cannot be empty';
  else if (changes.length === 0) saveTip = 'Make a change to save';
  else if (draft.reason === null) saveTip = 'Choose a reason for the correction';
  else if (draft.reason === ReceivingAdjustmentReasonEnum.Other && !draft.note.trim())
    saveTip = 'Add a note for the “Other” reason';

  const summaryLine =
    changes.length === 0
      ? 'No changes yet — lower received or reverse a bin.'
      : changes.length === 1
        ? changes[0].text
        : `${changes.length} changes · ${changes[0].text}`;

  // ── Mutators ────────────────────────────────────────────────────────────────
  const setReceived = (v: string) => {
    let n = parseInt(v, 10);
    if (Number.isNaN(n)) n = 0;
    if (n < 0) n = 0;
    patch((d) => ({ ...d, received: n }));
  };
  const stepReceived = (delta: number) => {
    if (readOnly) return;
    patch((d) => {
      let n = d.received + delta;
      if (n < 0) n = 0;
      if (n > poCap) n = poCap;
      return { ...d, received: n };
    });
  };
  const setPlacementQty = (pid: number, v: string) => {
    let n = parseInt(v, 10);
    if (Number.isNaN(n)) n = 0;
    if (n < 0) n = 0;
    patch((d) => ({
      ...d,
      placements: d.placements.map((p) => {
        if (p.id !== pid) return p;
        const q = Math.min(n, p.originalQty);
        return { ...p, qty: q, reversed: q === 0 ? p.reversed : false };
      }),
    }));
  };
  const reversePlacement = (pid: number) => {
    if (readOnly) return;
    patch((d) => ({
      ...d,
      placements: d.placements.map((p) => (p.id === pid ? { ...p, reversed: true, qty: 0 } : p)),
    }));
  };
  const undoPlacement = (pid: number) =>
    patch((d) => ({
      ...d,
      placements: d.placements.map((p) =>
        p.id === pid ? { ...p, reversed: false, qty: p.originalQty } : p
      ),
    }));
  const setSerial = (sid: number, v: string) =>
    patch((d) => ({ ...d, serials: d.serials.map((s) => (s.id === sid ? { ...s, sn: v } : s)) }));
  const removeSerial = (sid: number) => {
    if (readOnly) return;
    patch((d) => ({ ...d, serials: d.serials.map((s) => (s.id === sid ? { ...s, removed: true } : s)) }));
  };
  const undoSerial = (sid: number) =>
    patch((d) => ({ ...d, serials: d.serials.map((s) => (s.id === sid ? { ...s, removed: false } : s)) }));

  // ── Build the API request from the draft ─────────────────────────────────────
  const buildRequest = (): EditReceivedQuantityRequest => {
    const binReversals: PutAwayReversalDto[] = [];
    for (const p of draft.placements) {
      if (p.reversed && !p.committed)
        binReversals.push({ putAwayHistoryId: p.id, quantityToReverse: p.originalQty });
      else if (!p.reversed && p.qty < p.originalQty)
        binReversals.push({ putAwayHistoryId: p.id, quantityToReverse: p.originalQty - p.qty });
    }
    // Serial removal = a qty-1 reversal of that serial's put-away row.
    for (const s of draft.serials) {
      if (s.removed && !s.committed) binReversals.push({ putAwayHistoryId: s.id, quantityToReverse: 1 });
    }
    const serialEdits: SerialEditDto[] = [];
    for (const s of draft.serials) {
      if (!s.removed && s.sn.trim() !== s.originalSn)
        serialEdits.push({ putAwayHistoryId: s.id, newSerialNumber: s.sn.trim() });
    }
    return {
      newReceivedQuantity: draft.received,
      reason: draft.reason!,
      notes: draft.note.trim() || undefined,
      binReversals: binReversals.length ? binReversals : undefined,
      serialEdits: serialEdits.length ? serialEdits : undefined,
    };
  };

  const handleCommit = async () => {
    try {
      await onSubmit(item.id, buildRequest());
      setConfirmOpen(false);
      onOpenChange(false);
    } catch {
      // onSubmit surfaces the error (toast); keep the sheet open so the user can retry.
      setConfirmOpen(false);
    }
  };

  const reasonLabel =
    draft.reason !== null ? REASON_OPTIONS.find((o) => o.value === draft.reason)?.label ?? '—' : '—';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[680px]"
        >
          <TooltipProvider delayDuration={200}>
            {/* Header */}
            <div className="flex flex-shrink-0 items-start gap-3 border-b border-border p-4 pr-12">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-base font-bold">{item.itemCode}</span>
                  <span className="inline-flex h-5 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-semibold">
                    <type.Icon className="h-3 w-3 text-muted-foreground" />
                    {type.label}
                  </span>
                  {readOnly && (
                    <span className="inline-flex h-5 items-center gap-1 rounded-md bg-warning/15 px-2 text-xs font-semibold text-warning">
                      <Lock className="h-2.5 w-2.5" />
                      Read-only
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{item.itemDescription}</p>
              </div>
            </div>

            {/* Reconciliation readout */}
            {!isService && (
              <div className={cn('flex-shrink-0 border-b border-border p-4', READOUT_BG[readTone])}>
                <div className="flex flex-wrap items-center">
                  <div className="flex items-baseline gap-2 pr-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Received
                    </span>
                    <span className="text-lg font-bold tabular-nums">{draft.received}</span>
                  </div>
                  <Minus className="h-3 w-3 text-muted-foreground" />
                  <div className="flex items-baseline gap-2 px-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Placed
                    </span>
                    <span className="text-lg font-bold tabular-nums">{placed}</span>
                  </div>
                  <Equal className="h-3 w-3 text-muted-foreground" />
                  <div className="flex items-baseline gap-2 pl-4">
                    <span className={cn('text-xs font-semibold uppercase tracking-wide', TONE_TEXT[readTone])}>
                      Still to put away
                    </span>
                    <span className={cn('text-lg font-bold tabular-nums', TONE_TEXT[readTone])}>
                      {still}
                    </span>
                  </div>
                </div>
                {over && (
                  <div className="mt-2.5 flex items-center gap-1.5 text-xs font-medium text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Placed {placed} exceeds received {draft.received} — give back {placed - draft.received}{' '}
                    {unit} below before saving.
                  </div>
                )}
              </div>
            )}

            {/* Scroll body */}
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* Received editor */}
              <div className="mb-5">
                <div className="mb-2 flex items-center gap-1.5">
                  <span className="text-sm font-semibold">Received quantity</span>
                  <span className="text-xs text-muted-foreground">· what arrived</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div
                    className={cn(
                      'inline-flex h-10 items-stretch overflow-hidden rounded-md border',
                      over || overCap ? 'border-destructive' : 'border-input'
                    )}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => stepReceived(-1)}
                          disabled={readOnly}
                          aria-label="Decrease received quantity"
                          className="flex w-9 items-center justify-center border-r border-border bg-muted/50 text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Decrease</TooltipContent>
                    </Tooltip>
                    <input
                      type="number"
                      value={draft.received}
                      onChange={(e) => setReceived(e.target.value)}
                      disabled={readOnly}
                      aria-label="Received quantity"
                      className="w-24 border-0 bg-transparent text-center text-base font-bold tabular-nums text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => stepReceived(1)}
                          disabled={readOnly}
                          aria-label="Increase received quantity"
                          className="flex w-9 items-center justify-center border-l border-border bg-muted/50 text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Increase</TooltipContent>
                    </Tooltip>
                  </div>
                  <span className="text-sm text-muted-foreground">{unit}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    PO allows up to <b className="text-foreground tabular-nums">{poCap}</b>
                  </span>
                </div>
                {receivedMsg && (
                  <div className={cn('mt-2 flex items-center gap-1.5 text-xs', TONE_TEXT[receivedMsgTone])}>
                    {receivedMsgTone === 'destructive' ? (
                      <AlertTriangle className="h-3 w-3" />
                    ) : (
                      <Info className="h-3 w-3" />
                    )}
                    {receivedMsg}
                  </div>
                )}
              </div>

              {/* Put-away breakdown (bin/lot + serial) */}
              {!isService && (
                <>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold">Put-away breakdown</span>
                      <span className="text-xs text-muted-foreground">· where it went</span>
                    </div>
                    {over && (
                      <span className="text-xs font-semibold text-destructive">
                        Reduce {placed - draft.received} {unit} to save
                      </span>
                    )}
                  </div>

                  {/* BIN / LOT rows */}
                  {isBinLot && (
                    <div className="flex flex-col gap-2">
                      {draft.placements.length === 0 && (
                        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
                          Nothing placed yet — put-away happens on the mobile app.
                        </div>
                      )}
                      {draft.placements.map((p) => {
                        const reducedNotReversed = !p.reversed && p.qty < p.originalQty;
                        const tagLabel = p.reversed
                          ? p.committed
                            ? 'Reversed'
                            : 'Reversing'
                          : reducedNotReversed
                            ? 'Reduced'
                            : '';
                        const wasLabel = reducedNotReversed
                          ? `was ${p.originalQty} ${unit}`
                          : p.reversed && !p.committed
                            ? `freed ${p.originalQty} ${unit}`
                            : '';
                        return (
                          <div
                            key={p.id}
                            className={cn(
                              'flex items-center gap-3 rounded-lg border px-3 py-2.5',
                              p.reversed
                                ? 'border-border bg-muted/40'
                                : over
                                  ? 'border-destructive/40 bg-card'
                                  : 'border-border bg-card'
                            )}
                          >
                            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div
                                className={cn(
                                  'font-mono text-sm font-semibold',
                                  p.reversed ? 'text-muted-foreground line-through' : 'text-foreground'
                                )}
                              >
                                {p.bin}
                              </div>
                              <div className="mt-0.5 flex items-center gap-2">
                                {p.lot && (
                                  <span className="font-mono text-xs text-muted-foreground">Lot {p.lot}</span>
                                )}
                                {tagLabel && (
                                  <span
                                    className={cn(
                                      'inline-flex h-4 items-center rounded px-1.5 text-[10px] font-semibold',
                                      p.reversed
                                        ? 'bg-destructive/15 text-destructive'
                                        : 'bg-warning/15 text-warning'
                                    )}
                                  >
                                    {tagLabel}
                                  </span>
                                )}
                                {wasLabel && <span className="text-xs text-muted-foreground">{wasLabel}</span>}
                              </div>
                            </div>
                            {!p.reversed && (
                              <>
                                <Input
                                  type="number"
                                  value={p.qty}
                                  min={0}
                                  max={p.originalQty}
                                  onChange={(e) => setPlacementQty(p.id, e.target.value)}
                                  disabled={readOnly}
                                  aria-label={`Quantity in ${p.bin}`}
                                  className="h-9 w-16 text-center text-sm font-semibold tabular-nums"
                                />
                                <span className="w-6 text-xs text-muted-foreground">{unit}</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => reversePlacement(p.id)}
                                  disabled={readOnly}
                                  className="h-9 gap-1.5 text-destructive"
                                >
                                  <Undo2 className="h-3 w-3" />
                                  Reverse
                                </Button>
                              </>
                            )}
                            {p.reversed && !p.committed && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => undoPlacement(p.id)}
                                disabled={readOnly}
                                className="h-9 gap-1.5"
                              >
                                <RotateCcw className="h-3 w-3" />
                                Undo
                              </Button>
                            )}
                            {p.reversed && p.committed && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help text-xs text-muted-foreground">audit</span>
                                </TooltipTrigger>
                                <TooltipContent>Reversed in an earlier correction. Kept as an audit record.</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isBinLot && fullyReversed && (
                    <div className="mt-2.5 flex items-center gap-2 rounded-lg border border-dashed border-warning/40 bg-warning/5 px-3 py-2.5">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-warning" />
                      <span className="text-xs text-muted-foreground">
                        Every bin is reversed — all <b className="text-foreground">{draft.received} {unit}</b> now
                        need re-binning on the mobile app.
                      </span>
                    </div>
                  )}

                  {/* SERIAL rows */}
                  {isSerial && (
                    <div className="flex flex-col gap-2">
                      {draft.serials.map((s) => {
                        const isDup = dupIds.has(s.id);
                        const empty = !s.sn.trim();
                        const tagLabel = s.removed
                          ? s.committed
                            ? 'Removed'
                            : 'Removing'
                          : s.sn.trim() !== s.originalSn
                            ? 'Edited'
                            : '';
                        return (
                          <div
                            key={s.id}
                            className={cn(
                              'flex items-center gap-3 rounded-lg border px-3 py-2.5',
                              s.removed
                                ? 'border-border bg-muted/40'
                                : isDup
                                  ? 'border-destructive/50 bg-card'
                                  : 'border-border bg-card'
                            )}
                          >
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                              <ScanBarcode className="h-4 w-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              {!s.removed ? (
                                <Input
                                  value={s.sn}
                                  onChange={(e) => setSerial(s.id, e.target.value)}
                                  disabled={readOnly}
                                  spellCheck={false}
                                  aria-label={`Serial number (was ${s.originalSn})`}
                                  className={cn(
                                    'h-8 max-w-56 font-mono text-sm font-semibold',
                                    isDup || empty ? 'border-destructive' : 'border-input'
                                  )}
                                />
                              ) : (
                                <span className="font-mono text-sm font-semibold text-muted-foreground line-through">
                                  {s.sn}
                                </span>
                              )}
                              <div className="mt-1 flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {s.bin}
                                </span>
                                {tagLabel && (
                                  <span
                                    className={cn(
                                      'inline-flex h-4 items-center rounded px-1.5 text-[10px] font-semibold',
                                      s.removed ? 'bg-destructive/15 text-destructive' : 'bg-info/15 text-info'
                                    )}
                                  >
                                    {tagLabel}
                                  </span>
                                )}
                              </div>
                              {!s.removed && (isDup || empty) && (
                                <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-destructive">
                                  <AlertTriangle className="h-3 w-3" />
                                  {isDup
                                    ? 'Duplicate — this serial is already on this item.'
                                    : 'Serial can’t be empty.'}
                                </div>
                              )}
                            </div>
                            {!s.removed && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeSerial(s.id)}
                                disabled={readOnly}
                                className="h-8 gap-1.5 text-destructive"
                              >
                                <X className="h-3 w-3" />
                                Remove
                              </Button>
                            )}
                            {s.removed && !s.committed && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => undoSerial(s.id)}
                                disabled={readOnly}
                                className="h-8 gap-1.5"
                              >
                                <RotateCcw className="h-3 w-3" />
                                Undo
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* mobile nudge */}
                  <div className="mt-3.5 flex items-center gap-2.5 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2.5">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">
                      <Smartphone className="h-4 w-4" />
                    </span>
                    <div className="text-xs leading-relaxed text-muted-foreground">
                      Need to place stock in a <b className="text-foreground">new bin</b> or add a serial? That’s
                      done on the <b className="text-foreground">mobile app</b> at the shelf — this screen only
                      corrects and reverses.
                    </div>
                  </div>
                </>
              )}

              {/* Reason for correction */}
              <div className="mt-4 border-t border-border pt-4">
                <div className="mb-2 flex items-center gap-1.5">
                  <span className="text-sm font-semibold">Reason for correction</span>
                  <span className="font-bold text-destructive">*</span>
                  <span className="text-xs text-muted-foreground">· audited</span>
                </div>
                <Select
                  value={draft.reason !== null ? String(draft.reason) : ''}
                  onValueChange={(v) => patch((d) => ({ ...d, reason: Number(v) as ReceivingAdjustmentReasonEnum }))}
                  disabled={readOnly}
                >
                  <SelectTrigger
                    aria-label="Reason for correction"
                    className={cn(
                      changes.length > 0 && draft.reason === null ? 'border-destructive' : 'border-input'
                    )}
                  >
                    <SelectValue placeholder="Select a reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  value={draft.note}
                  onChange={(e) => patch((d) => ({ ...d, note: e.target.value }))}
                  disabled={readOnly}
                  maxLength={500}
                  placeholder={
                    draft.reason === ReceivingAdjustmentReasonEnum.Other
                      ? 'Required — describe the correction…'
                      : 'Optional note…'
                  }
                  className={cn(
                    'mt-2 min-h-14',
                    draft.reason === ReceivingAdjustmentReasonEnum.Other && changes.length > 0 && !draft.note.trim()
                      ? 'border-destructive'
                      : 'border-input'
                  )}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-border p-3.5">
              {readOnly ? (
                <div className="flex items-center gap-2.5">
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="flex-1 text-xs text-muted-foreground">
                    Locked while posting to NetSuite is in progress. Corrections resume once it settles.
                  </span>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3.5">
                  <div className="min-w-0 flex-1 text-xs text-muted-foreground">{summaryLine}</div>
                  <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span keeps the tooltip working while the button is disabled */}
                      <span>
                        <Button onClick={() => setConfirmOpen(true)} disabled={!canSave} className="gap-1.5">
                          <Check className="h-4 w-4" />
                          Save correction
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{saveTip}</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </TooltipProvider>
        </SheetContent>
      </Sheet>

      {/* Save confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardCheck className="h-4 w-4" />
              </span>
              Confirm correction
            </DialogTitle>
            <DialogDescription>
              Review before saving to <b className="font-mono text-foreground">{item.itemCode}</b>. Reversed
              records are kept for audit; freed stock is re-binned on mobile.
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/35 px-3 py-2.5">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-info/15 text-info">
                <Info className="h-3 w-3" />
              </span>
              <span className="text-xs leading-relaxed text-foreground">
                <b>Reason:</b> {reasonLabel}
                {draft.note.trim() && (
                  <span className="mt-0.5 block text-muted-foreground">“{draft.note.trim()}”</span>
                )}
              </span>
            </div>
            {changes.map((c, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2.5">
                <span
                  className={cn(
                    'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
                    TONE_BG_SOFT[c.tone],
                    TONE_TEXT[c.tone]
                  )}
                >
                  <c.icon className="h-3 w-3" />
                </span>
                <span className="text-xs leading-relaxed text-foreground">{c.text}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Back
            </Button>
            <Button onClick={handleCommit} disabled={submitting} className="gap-1.5">
              {submitting ? (
                'Saving…'
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Save correction
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
